// Dev tool for generating and saving game background images.

import React, { useState } from 'react';
import { gameLibrary } from '@airhead-games/shared/configs';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ImagePlusIcon,
  SaveIcon,
} from 'lucide-react';
import { useAuth } from '../hooks';
import { generateDevImage, saveDevImage } from '../transport/api/protectedApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { InputGroup, InputGroupTextarea } from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';

interface Candidate {
  index: number;
  base64: string;
  mimeType: string;
  sizeKb: number;
}

function toSnakeCase(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/(^_|_$)/g, '');
}

const BackgroundChooser: React.FC = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFileName, setSavedFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  const selectedGame = gameLibrary.find(
    (g) => g.gameCore.gameId === selectedGameId
  );

  const handleGenerate = async () => {
    if (!user) return;

    setGenerating(true);
    setError(null);
    setCandidate(null);
    setRequestId(null);
    setSavedFileName(null);

    try {
      const result = await generateDevImage({
        type: 'background',
        notes: notes || undefined,
        userPreferences: user.userPreferences,
      });
      setRequestId(result.data.requestId);
      setCandidate(result.data.candidates[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!selectedGame || !requestId || !candidate) return;

    setSaving(true);
    setError(null);

    try {
      const gameName = toSnakeCase(selectedGame.gameCore.gameName);

      const result = await saveDevImage({
        type: 'background',
        requestId,
        index: candidate.index,
        gameName,
      });
      setSavedFileName(result.data.fileName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="fixed inset-0 overflow-hidden bg-muted text-foreground">
      {candidate ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(data:${candidate.mimeType};base64,${candidate.base64})`,
          }}
          aria-hidden="true"
        />
      ) : (
        <div className="absolute inset-0 bg-muted" aria-hidden="true" />
      )}

      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="absolute top-4 left-4 z-10 w-[min(calc(100vw-2rem),28rem)]"
      >
        {!open && (
          <CollapsibleTrigger asChild>
            <Button type="button" variant="secondary" size="icon">
              <ChevronRightIcon />
              <span className="sr-only">Open background generator</span>
            </Button>
          </CollapsibleTrigger>
        )}

        <CollapsibleContent>
          <Card className="bg-card/95 shadow-lg backdrop-blur">
            <CardHeader>
              <CardTitle>Background Generator</CardTitle>
              <CardDescription>
                Generate a full-screen game background and save it to the shared
                image bucket.
              </CardDescription>
              <CardAction>
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="ghost" size="icon-sm">
                    <ChevronDownIcon />
                    <span className="sr-only">
                      Collapse background generator
                    </span>
                  </Button>
                </CollapsibleTrigger>
              </CardAction>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <FieldGroup>
                <Field>
                  <FieldLabel>Scene notes</FieldLabel>
                  <InputGroup>
                    <InputGroupTextarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Describe the scene..."
                      rows={3}
                    />
                  </InputGroup>
                  <FieldDescription>
                    Optional generation guidance.
                  </FieldDescription>
                </Field>
              </FieldGroup>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? (
                    <Spinner data-icon="inline-start" />
                  ) : (
                    <ImagePlusIcon data-icon="inline-start" />
                  )}
                  {generating ? 'Generating...' : 'Generate'}
                </Button>

                {candidate && (
                  <Badge variant="secondary">{candidate.sizeKb} KB</Badge>
                )}
              </div>

              {candidate && (
                <Field>
                  <FieldLabel>Save for game</FieldLabel>
                  <Select
                    value={selectedGameId}
                    onValueChange={setSelectedGameId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a game to save..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {gameLibrary.map((g) => (
                          <SelectItem
                            key={g.gameCore.gameId}
                            value={g.gameCore.gameId}
                          >
                            {g.gameCore.gameName}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {candidate && selectedGameId && (
                <div>
                  <Button type="button" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <Spinner data-icon="inline-start" />
                    ) : (
                      <SaveIcon data-icon="inline-start" />
                    )}
                    {saving ? 'Saving...' : 'Save to Bucket'}
                  </Button>
                </div>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertTitle>Request failed</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {savedFileName && (
                <Alert>
                  <AlertTitle>Saved</AlertTitle>
                  <AlertDescription>
                    <code>{savedFileName}</code>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </main>
  );
};

export default BackgroundChooser;
